import { useCallback, useEffect, useState } from 'react';
import { HoaDonBanHang, HoaDonBanHangInput, HoaDonBanHangResponse } from '@/types/schemas/hoa-don-ban-hang.type';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import api from '@/lib/api';

interface UseHoaDonBanHangReturn {
  hoaDonBanHangs: HoaDonBanHang[];
  isLoading: boolean;
  isLoadingDetail: boolean;
  addHoaDonBanHang: (newHoaDonBanHang: HoaDonBanHangInput) => Promise<HoaDonBanHang>;
  updateHoaDonBanHang: (uuid: string, updatedHoaDonBanHang: HoaDonBanHangInput) => Promise<HoaDonBanHang>;
  deleteHoaDonBanHang: (uuid: string) => Promise<void>;
  refreshHoaDonBanHangs: () => Promise<void>;
  getHoaDonBanHangByCustomer: (customerId: string) => Promise<HoaDonBanHang[]>;
  getHoaDonBanHangDetail: (uuid: string) => Promise<[]>;
  filterHoaDonBanHangs: (
    filters: Record<string, any>,
    page?: number,
    pageSize?: number
  ) => Promise<{ results: HoaDonBanHang[]; count: number }>;
}

/**
 * Hook for managing HoaDonBanHang (Sales Invoice) data
 *
 * This hook provides functions to fetch, create, update, and delete sales invoices and their details.
 */
export const useHoaDonBanHang = (initialHoaDonBanHangs: HoaDonBanHang[] = []): UseHoaDonBanHangReturn => {
  const [hoaDonBanHangs, setHoaDonBanHangs] = useState<HoaDonBanHang[]>(initialHoaDonBanHangs);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(true);

  const { entity } = useAuth();

  const fetchHoaDonBanHangs = async () => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonBanHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/`
      );
      setHoaDonBanHangs(response.data.results);
    } catch (error) {
      console.error('Error fetching sales invoices:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonBanHangByCustomer = async (customerId: string): Promise<HoaDonBanHang[]> => {
    if (!entity?.slug) return [];

    setIsLoading(true);
    try {
      const response = await api.get<HoaDonBanHangResponse>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/?ma_kh=${customerId}`
      );
      return response.data.results;
    } catch (error) {
      console.error('Error fetching sales invoices by customer:', error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  const addHoaDonBanHang = async (newHoaDonBanHang: HoaDonBanHangInput): Promise<HoaDonBanHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.post<HoaDonBanHang>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/`,
        newHoaDonBanHang
      );

      const addedHoaDonBanHang = response.data;
      setHoaDonBanHangs(prev => [...prev, addedHoaDonBanHang]);
      return addedHoaDonBanHang;
    } catch (error) {
      console.error('Error adding sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateHoaDonBanHang = async (
    uuid: string,
    updatedHoaDonBanHang: HoaDonBanHangInput
  ): Promise<HoaDonBanHang> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoading(true);
    try {
      const response = await api.put<HoaDonBanHang>(
        `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/${uuid}/`,
        updatedHoaDonBanHang
      );

      const updatedData = response.data;
      setHoaDonBanHangs(prev => prev.map(item => (item.uuid === uuid ? updatedData : item)));
      return updatedData;
    } catch (error) {
      console.error('Error updating sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteHoaDonBanHang = async (uuid: string) => {
    if (!entity?.slug) return;

    setIsLoading(true);
    try {
      await api.delete(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/${uuid}/`);
      setHoaDonBanHangs(prev => prev.filter(item => item.uuid !== uuid));
    } catch (error) {
      console.error('Error deleting sales invoice:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getHoaDonBanHangDetail = async (uuid: string): Promise<[]> => {
    if (!entity?.slug) throw new Error('Entity not found');

    setIsLoadingDetail(true);
    try {
      const response = await api.get<[]>(`/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/${uuid}/`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sales invoice detail:', error);
      throw error;
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const filterHoaDonBanHangs = useCallback(
    async (
      filters: Record<string, any>,
      page: number = 0,
      pageSize: number = 30
    ): Promise<{ results: HoaDonBanHang[]; count: number }> => {
      if (!entity?.slug) return { results: [], count: 0 };

      try {
        const response = await api.post(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/filter/?page=${page + 1}&page_size=${pageSize}`,
          filters
        );

        if (Array.isArray(response.data)) {
          return { results: response.data, count: response.data.length };
        }

        if (response.data?.results) {
          return { results: response.data.results, count: response.data.count || 0 };
        }

        return { results: [], count: 0 };
      } catch (error) {
        console.error('Error filtering sales invoices via filter API:', error);
        return { results: [], count: 0 };
      }
    },
    [entity?.slug]
  );

  useEffect(() => {
    fetchHoaDonBanHangs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.slug]);

  return {
    hoaDonBanHangs,
    isLoading,
    isLoadingDetail,
    addHoaDonBanHang,
    updateHoaDonBanHang,
    deleteHoaDonBanHang,
    refreshHoaDonBanHangs: fetchHoaDonBanHangs,
    getHoaDonBanHangByCustomer,
    getHoaDonBanHangDetail,
    filterHoaDonBanHangs
  };
};

export const fetchAllHoaDonBanHang = async (
  entitySlug: string,
  filters: Record<string, any> = {},
  pageSize = 200
): Promise<any[]> => {
  let page = 0;
  let all: any[] = [];
  let totalCount = 0;

  while (true) {
    const response = await api.post<any>(
      `/entities/${entitySlug}/erp/${QUERY_KEYS.HOA_DON_BAN_HANG}/filter/`,
      filters,
      {
        params: {
          page: page + 1,
          page_size: pageSize
        }
      }
    );

    const raw = response.data;

    const pageResults = Array.isArray(raw) ? raw : raw.results || [];
    const count = Array.isArray(raw) ? pageResults.length : (raw.count ?? totalCount);

    totalCount = count ?? totalCount;
    all = all.concat(pageResults);

    if (!pageResults.length) {
      break;
    }

    const hasNext = (response.data as any).next;

    if (!hasNext && pageResults.length < pageSize) {
      break;
    }

    if (totalCount && all.length >= totalCount) {
      break;
    }

    page += 1;
  }

  return all;
};
